import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import BusinessIcon from "@mui/icons-material/BusinessOutlined"
import FileIcon from "@mui/icons-material/InsertDriveFileOutlined"
import FolderIcon from "@mui/icons-material/FolderOutlined"
import GroupIcon from "@mui/icons-material/GroupsOutlined"
import LinkIcon from "@mui/icons-material/LinkOutlined"
import LockIcon from "@mui/icons-material/LockOutlined"

import { driveApi, roleApi, userApi } from "../../api"
import { useAuth } from "../../context/AuthContext"
import UserAvatar from "../../components/UserAvatar"
import { LoadingRows } from "../../components/LoadingState"
import { rise } from "../../motion"

/**
 * Who can open this, and who can change it — shaped like a shared drive's
 * share dialog, because that is the one everybody already knows.
 *
 * ── The four parts, in the order people read them ───────────────────────────
 *
 * 1. Add people: one box for people AND roles, several at once, then the access
 *    they get. Handing a folder to the three people on an engagement is one
 *    action, not three trips through a form.
 * 2. People with access: the owner first, then everyone else, each with their
 *    access right there — change it, or take it away, on the row.
 * 3. General access: restricted, or everybody in the organization. It is a
 *    different kind of decision from naming people, so it is its own section
 *    rather than a third option in a dropdown.
 * 4. Copy link, for a folder: the address of the folder itself. It opens for
 *    whoever already has access and nobody else — a link here is directions,
 *    never a key.
 *
 * ── Read-only for everybody but the owner ───────────────────────────────────
 *
 * Anyone who can see the item can see who else can, which is the question
 * people actually come here with. Only the owner (or an administrator) can
 * change it, and the dialog says that up front rather than offering controls
 * that end in a refusal. The server remains the authority either way.
 */

/** Our two levels, in the words a shared drive uses for them. */
const ACCESS_LABEL = { view: "Viewer", edit: "Editor" }

function withoutMe(rows, viewerId) {
  return (rows || []).filter((person) => Number(person.id) !== Number(viewerId))
}

export default function ShareDialog({ item, clientId, storeLabel, onClose, onChanged }) {
  const { user } = useAuth()

  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const [people, setPeople] = useState([])
  const [roles, setRoles] = useState([])
  const [picked, setPicked] = useState([])
  const [newAccess, setNewAccess] = useState("view")
  const searchTimer = useRef(null)

  const scope = useMemo(() => (clientId ? { client_id: clientId } : {}), [clientId])
  const orgName = storeLabel || user?.organization_label || "your organization"

  /* Refetched in place after every change: the list stays on screen and a bar
     runs across the top, the same as a checklist ticking over. */
  const load = useCallback(async () => {
    if (!item) return
    try {
      const payload = await driveApi.shares({ ...scope, item_type: item.kind, item_id: item.id })
      setData(payload)
    } catch (err) {
      setError(err.userMessage || "The sharing list could not be loaded.")
      setData((current) => current || { shares: [], owner: null, can_manage: false })
    }
  }, [item, scope])

  useEffect(() => {
    setData(null)
    setPicked([])
    setNewAccess("view")
    setError(null)
    setNotice(null)
    load()
  }, [load])

  const shares = data?.shares || []
  const canManage = Boolean(data?.can_manage)

  /* Only for the person who can add anyone. Somebody looking at who has
     access needs neither list, and may not be allowed the roles one at all. */
  useEffect(() => {
    if (!item || !canManage) return
    userApi
      .directory("")
      .then((payload) => setPeople(withoutMe(payload.users, user?.id)))
      .catch(() => setPeople([]))
    roleApi
      .list()
      .then((payload) => setRoles(payload.roles || []))
      .catch(() => setRoles([]))
  }, [item, canManage, user?.id])
  const named = shares.filter((share) => share.subject_type !== "organization")
  const orgShare = shares.find((share) => share.subject_type === "organization") || null

  /* Who is already on the list, so the picker does not offer them again. */
  const alreadyIn = useMemo(
    () => new Set(named.map((share) => `${share.subject_type}:${share.subject_id}`)),
    [named],
  )

  const options = useMemo(
    () => [
      ...people
        .filter((person) => !alreadyIn.has(`user:${person.id}`))
        .map((person) => ({
          type: "user",
          id: String(person.id),
          label: person.name || person.username,
          detail: person.email || person.username,
          avatarKey: person.avatar_key,
        })),
      ...roles
        .filter((role) => !alreadyIn.has(`role:${role.id}`))
        .map((role) => ({ type: "role", id: String(role.id), label: role.label, detail: "Everyone with this role" })),
    ],
    [people, roles, alreadyIn],
  )

  const run = async (work, message) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await work()
      await load()
      onChanged?.()
      if (message) setNotice(message)
    } catch (err) {
      setError(err.userMessage || "That change could not be saved.")
      await load()
    } finally {
      setBusy(false)
    }
  }

  const shareWithPicked = () =>
    run(async () => {
      for (const subject of picked) {
        await driveApi.share({
          ...scope,
          item_type: item.kind,
          item_id: item.id,
          subject_type: subject.type,
          subject_id: subject.id,
          access: newAccess,
        })
      }
      setPicked([])
    }, `Shared with ${picked.length === 1 ? picked[0].label : `${picked.length} people and roles`}.`)

  /* Our POST is an upsert, so changing someone's access is sharing again. */
  const changeAccess = (share, access) =>
    access === "remove"
      ? run(() => driveApi.unshare(share.id, clientId ? { client_id: clientId } : undefined), `${share.subject_label} no longer has access.`)
      : run(() =>
          driveApi.share({
            ...scope,
            item_type: item.kind,
            item_id: item.id,
            subject_type: share.subject_type,
            subject_id: share.subject_id,
            access,
          }),
        )

  const setGeneral = (value, access = orgShare?.access || "view") =>
    value === "restricted"
      ? orgShare
        ? run(() => driveApi.unshare(orgShare.id, clientId ? { client_id: clientId } : undefined), "Only people with access can open it now.")
        : null
      : run(
          () =>
            driveApi.share({
              ...scope,
              item_type: item.kind,
              item_id: item.id,
              subject_type: "organization",
              subject_id: String(clientId || user?.organization_id),
              access,
            }),
          `Everybody in ${orgName} can ${access === "edit" ? "edit" : "open"} it.`,
        )

  const copyLink = async () => {
    const link = `${window.location.origin}/files?folder=${item.id}${clientId ? `&client=${encodeURIComponent(clientId)}` : ""}`
    try {
      await navigator.clipboard.writeText(link)
      setNotice("Link copied. It opens for people who already have access.")
    } catch {
      setNotice(link)
    }
  }

  const Icon = item?.kind === "folder" ? FolderIcon : FileIcon
  const owner = data?.owner
  const ownerIsMe = owner && Number(owner.id) === Number(user?.id)

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar variant="rounded" sx={{ bgcolor: item?.kind === "folder" ? "#e9a13b22" : "action.hover", color: item?.kind === "folder" ? "#b9771a" : "text.secondary" }}>
            <Icon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="p" noWrap>
              Share “{item?.name}”
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item?.kind === "folder"
                ? "Everything in this folder goes with it, including anything added later."
                : "This one document, and nothing else."}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Box sx={{ height: 3 }}>{busy ? <LinearProgress sx={{ height: 3 }} /> : null}</Box>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        ) : null}

        {data === null ? (
          <LoadingRows rows={3} height={40} />
        ) : (
          <>
            {/* 1. Add people and roles — the owner's controls only. */}
            {canManage ? (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2.5 }}>
                <Autocomplete
                  multiple
                  fullWidth
                  options={options}
                  value={picked}
                  onChange={(_event, value) => setPicked(value)}
                  groupBy={(option) => (option.type === "user" ? "People" : "Roles")}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
                  filterOptions={(list, { inputValue }) => {
                    const term = inputValue.trim().toLowerCase()
                    return term ? list.filter((option) => `${option.label} ${option.detail}`.toLowerCase().includes(term)) : list
                  }}
                  onInputChange={(_event, term, reason) => {
                    if (reason !== "input") return
                    // The directory is served a page at a time: ask it, rather
                    // than telling somebody their colleague does not exist.
                    clearTimeout(searchTimer.current)
                    searchTimer.current = setTimeout(() => {
                      userApi
                        .directory(term)
                        .then((payload) => setPeople(withoutMe(payload.users, user?.id)))
                        .catch(() => {})
                    }, 250)
                  }}
                  renderOption={({ key, ...props }, option) => (
                    <Box component="li" key={key} {...props} sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
                      {option.type === "user" ? (
                        <UserAvatar name={option.label} avatarKey={option.avatarKey} size={30} tooltip={false} />
                      ) : (
                        <Avatar sx={{ width: 30, height: 30, bgcolor: "action.selected", color: "text.secondary" }}>
                          <GroupIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {option.detail}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getItemProps({ index })
                      return (
                        <Chip
                          key={key}
                          size="small"
                          label={option.label}
                          icon={option.type === "role" ? <GroupIcon /> : undefined}
                          {...chipProps}
                        />
                      )
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Add people or roles" placeholder={picked.length ? "" : "Name, email or role"} />
                  )}
                  noOptionsText="Nobody matches — or they already have access"
                />
                {picked.length > 0 ? (
                  <Stack spacing={1} sx={{ flexShrink: 0, pt: 0.25 }}>
                    <Select size="small" value={newAccess} onChange={(event) => setNewAccess(event.target.value)} sx={{ minWidth: 110 }}>
                      <MenuItem value="view">Viewer</MenuItem>
                      <MenuItem value="edit">Editor</MenuItem>
                    </Select>
                    <Button variant="contained" onClick={shareWithPicked} disabled={busy}>
                      Share
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Only {owner?.name || "the owner"} can change who has access. This is who has it now.
              </Alert>
            )}

            {/* 2. People with access. */}
            <Typography variant="body2" fontWeight={650} sx={{ mb: 1 }}>
              People with access
            </Typography>
            <Stack spacing={0.25} sx={{ mb: 2.5 }}>
              {owner ? (
                <AccessRow
                  index={0}
                  avatar={<UserAvatar name={owner.name} avatarKey={owner.avatar_key} size={34} tooltip={false} />}
                  title={`${owner.name}${ownerIsMe ? " (you)" : ""}`}
                  detail={owner.email || owner.username}
                  right={
                    <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                      Owner
                    </Typography>
                  }
                />
              ) : null}
              {named.map((share, index) => (
                <AccessRow
                  key={share.id}
                  index={index + 1}
                  avatar={
                    share.subject_type === "user" ? (
                      <UserAvatar name={share.subject_label} avatarKey={share.avatar_key} size={34} tooltip={false} />
                    ) : (
                      <Avatar sx={{ width: 34, height: 34, bgcolor: "action.selected", color: "text.secondary" }}>
                        <GroupIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                    )
                  }
                  title={`${share.subject_label}${Number(share.subject_id) === Number(user?.id) && share.subject_type === "user" ? " (you)" : ""}`}
                  detail={share.subject_type === "role" ? "Everyone with this role" : share.subject_detail}
                  right={
                    canManage ? (
                      <Select
                        size="small"
                        variant="standard"
                        disableUnderline
                        value={share.access}
                        onChange={(event) => changeAccess(share, event.target.value)}
                        disabled={busy}
                        inputProps={{ "aria-label": `Access for ${share.subject_label}` }}
                        sx={{ fontSize: 14, minWidth: 92, "& .MuiSelect-select": { py: 0.5, pr: "24px !important", textAlign: "right" } }}
                      >
                        <MenuItem value="view">Viewer</MenuItem>
                        <MenuItem value="edit">Editor</MenuItem>
                        <Divider />
                        <MenuItem value="remove" sx={{ color: "error.main" }}>
                          Remove access
                        </MenuItem>
                      </Select>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                        {ACCESS_LABEL[share.access]}
                      </Typography>
                    )
                  }
                />
              ))}
              {named.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                  Nobody else yet.
                </Typography>
              ) : null}
            </Stack>

            {/* 3. General access. */}
            <Typography variant="body2" fontWeight={650} sx={{ mb: 1 }}>
              General access
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: orgShare ? "success.light" : "action.selected",
                  color: orgShare ? "success.dark" : "text.secondary",
                }}
              >
                {orgShare ? <BusinessIcon sx={{ fontSize: 19 }} /> : <LockIcon sx={{ fontSize: 18 }} />}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {canManage ? (
                  <Select
                    size="small"
                    variant="standard"
                    disableUnderline
                    value={orgShare ? "organization" : "restricted"}
                    onChange={(event) => setGeneral(event.target.value)}
                    disabled={busy}
                    inputProps={{ "aria-label": "General access" }}
                    sx={{ fontWeight: 600, fontSize: 14, "& .MuiSelect-select": { py: 0.25 } }}
                  >
                    <MenuItem value="restricted">Restricted</MenuItem>
                    <ListSubheader sx={{ lineHeight: "28px" }}>Organization</ListSubheader>
                    <MenuItem value="organization">Everybody in {orgName}</MenuItem>
                  </Select>
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    {orgShare ? `Everybody in ${orgName}` : "Restricted"}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {orgShare
                    ? `Anyone in ${orgName} can ${orgShare.access === "edit" ? "open and change" : "open"} it`
                    : "Only the people above can open it"}
                </Typography>
              </Box>
              {orgShare ? (
                canManage ? (
                  <Select
                    size="small"
                    variant="standard"
                    disableUnderline
                    value={orgShare.access}
                    onChange={(event) => setGeneral("organization", event.target.value)}
                    disabled={busy}
                    inputProps={{ "aria-label": "Access for everybody" }}
                    sx={{ fontSize: 14, minWidth: 92, "& .MuiSelect-select": { py: 0.5, pr: "24px !important", textAlign: "right" } }}
                  >
                    <MenuItem value="view">Viewer</MenuItem>
                    <MenuItem value="edit">Editor</MenuItem>
                  </Select>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                    {ACCESS_LABEL[orgShare.access]}
                  </Typography>
                )
              ) : null}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {item?.kind === "folder" ? (
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={copyLink} sx={{ borderRadius: 999 }}>
            Copy link
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onClose} sx={{ borderRadius: 999, px: 3 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** One person, role or owner on the access list. */
function AccessRow({ avatar, title, detail, right, index }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 0.75, px: 0.5, borderRadius: 1.5, "&:hover": { bgcolor: "action.hover" }, ...rise(index) }}
    >
      {avatar}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {title}
        </Typography>
        {detail ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {detail}
          </Typography>
        ) : null}
      </Box>
      {right}
    </Stack>
  )
}
